import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSheetTemplates } from 'services/api/project';
import { Grid, Dialog, Divider, IconButton, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SheetTemplateCard from 'layouts/projects/Forms/SheetTemplates/SheetTemplateCard';
import SheetTemplateForm from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm';
import TemplateCrud from 'layouts/projects/Forms/SheetTemplates/SheetTemplateCRUD';
import AaroLoadingScreen from 'components/Loading';

// Supabase renk paleti
const colors = {
    brand: '#3ECF8E',
    brandHover: '#24b47e',
    background: '#FFFFFF',
    backgroundAlt: '#F8F9FA',
    border: '#E9ECEF',
    text: '#1F1F1F',
    textLight: '#4A5568',
    textMuted: '#718096',
    hover: '#F8F9FA',
    divider: '#EDF2F7',
};

const SupaDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        margin: 'auto',
        width: '95%',
        height: '95%',
        maxWidth: '95%',
        maxHeight: '95%',
        borderRadius: '6px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: `1px solid ${colors.border}`,
    },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
}));

const DialogTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: colors.text,
}));

const DialogContent = styled(Box)(({ theme }) => ({
    padding: '16px',
    height: 'calc(100% - 56px)',
    overflowY: 'auto',
    backgroundColor: colors.backgroundAlt,
}));

const CategoryTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.text,
    marginBottom: '4px',
}));

const CategoryDescription = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: colors.textMuted,
    marginBottom: '12px',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    width: '28px',
    height: '28px',
    color: colors.textLight,
    padding: '4px',
    '&:hover': {
        backgroundColor: colors.hover,
        color: colors.text,
    },
}));

const BackButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    color: colors.brand,
    cursor: 'pointer',
    marginRight: '12px',
    '&:hover': {
        textDecoration: 'underline',
        color: colors.brandHover,
    },
}));

const GridContainer = styled(Grid)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    margin: '0 -8px',
}));

const GridItem = styled(Grid)(({ theme }) => ({
    padding: '8px',
    width: '25%', // 4 kart yan yana
    [theme.breakpoints.down('md')]: {
        width: '33.333%', // Orta ekranlarda 3 kart
    },
    [theme.breakpoints.down('sm')]: {
        width: '50%', // Küçük ekranlarda 2 kart
    },
    [theme.breakpoints.down('xs')]: {
        width: '100%', // Çok küçük ekranlarda 1 kart
    },
    height: '280px', // Sabit kart yüksekliği
}));

const CategoryContainer = styled(Box)(({ theme }) => ({
    backgroundColor: colors.background,
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '24px',
    border: `1px solid ${colors.border}`,
}));

const fetchSheetTemplates = async () => {
    try {
        const {
            data: { result },
        } = await getSheetTemplates();
        return result;
    } catch (error) {
        throw error;
    }
};

const isEmptyJson = (json) => Object.keys(json).length === 0 && json.constructor === Object;

function SheetTemplates({ open, handleClose, demandID }) {
    const [selected, setSelected] = useState({});
    const [edit, setEdit] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchSheetTemplates'],
        queryFn: fetchSheetTemplates,
        staleTime: 600000,
    });

    if (isLoading) return <AaroLoadingScreen />;
    if (error) return `Bir Hata Oluştu: ${error.message}`;

    const user = JSON.parse(localStorage.getItem('user'));

    const groupByCategory = data.reduce((acc, cur) => {
        const { name } = cur.category;
        if (!acc[name]) acc[name] = [];
        acc[name].push(cur);
        return acc;
    }, {});

    const handleBackToTemplates = () => {
        setSelected({});
        setEdit(false);
    };

    const renderTemplateCategories = () =>
        Object.keys(groupByCategory).map((category, index) => (
            <CategoryContainer key={category}>
                <CategoryTitle>{category}</CategoryTitle>
                <CategoryDescription>{groupByCategory[category][0].categoryDescription}</CategoryDescription>

                <GridContainer container>
                    {groupByCategory[category].map((template) => (
                        <GridItem key={template.id} item>
                            <SheetTemplateCard
                                setSelected={setSelected}
                                sheetTemplate={template}
                                image={template.coverImage}
                                title={template.name}
                                description={template.description}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            </CategoryContainer>
        ));

    const renderContent = () => {
        if (!isEmptyJson(selected)) {
            return <SheetTemplateForm sheetTemplate={selected} demandID={demandID} handleClose={handleClose} />;
        }

        if (edit && user.role === 'superadmin') {
            return <TemplateCrud demandTemplates={data} />;
        }

        return renderTemplateCategories();
    };

    return (
        <SupaDialog onClose={handleClose} open={open}>
            <DialogHeader>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {!isEmptyJson(selected) && (
                        <BackButton onClick={handleBackToTemplates}>
                            <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Geri
                        </BackButton>
                    )}
                    <DialogTitle>
                        {!isEmptyJson(selected) ? 'Şablon Detayları' : edit ? 'Şablon Düzenleme' : 'Şablonlar'}
                    </DialogTitle>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {isEmptyJson(selected) && user.role === 'superadmin' && (
                        <ActionButton onClick={() => setEdit(!edit)}>
                            <EditIcon fontSize="small" />
                        </ActionButton>
                    )}

                    <ActionButton onClick={handleClose}>
                        <CloseIcon fontSize="small" />
                    </ActionButton>
                </Box>
            </DialogHeader>
            <DialogContent>{renderContent()}</DialogContent>
        </SupaDialog>
    );
}

export default SheetTemplates;
